/**
 * SettingsDropdown.tsx — Org & Platform settings dropdown (top-right header)
 *
 * Replaces the old OrgSettingsPage with a compact dropdown next to the org picker.
 * Shows org-level settings links for all users, plus platform settings for admins.
 *
 * @since 2026-06-20
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgContext } from '../contexts/OrgContext';

interface SettingsItem {
  label: string;
  to: string;
  icon: string;
  description: string;
}

const ORG_SETTINGS: SettingsItem[] = [
  { label: 'Payment Provider', to: '/provider', icon: '💳', description: 'Stripe Connect setup' },
  { label: 'Webhooks & Sync', to: '/webhooks', icon: '🔗', description: 'Endpoints & delivery' },
  { label: 'MCP Server', to: '/mcp-server', icon: '🤖', description: 'AI agent protocol' },
  { label: 'BYOD Database', to: '/byod', icon: '🗄️', description: 'External database' },
  { label: 'Enforcement', to: '/enforcement-setup', icon: '🛡️', description: 'Billing enforcement' },
  { label: 'Email Templates', to: '/email-templates', icon: '✉️', description: 'Transactional emails' },
];

const PLATFORM_SETTINGS: SettingsItem[] = [
  { label: 'Platform Dashboard', to: '/platform/dashboard', icon: '📊', description: 'System overview' },
  { label: 'Platform Settings', to: '/platform/settings', icon: '⚙️', description: 'Global configuration' },
  { label: 'Platform Health', to: '/platform/health', icon: '💚', description: 'Services & uptime' },
  { label: 'All Tenants', to: '/platform/tenants', icon: '🏢', description: 'Manage organizations' },
  { label: 'Platform Revenue', to: '/platform/revenue', icon: '💰', description: 'Cross-org revenue' },
];

export default function SettingsDropdown(): React.JSX.Element {
  const { isPlatformAdmin } = useOrgContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        title="Settings"
        className="flex items-center justify-center rounded-lg border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-xl border border-slate-200 bg-white shadow-xl">
          {/* Org Settings */}
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Organization Settings
            </p>
          </div>
          <div className="py-1">
            {ORG_SETTINGS.map((item) => (
              <button
                key={item.to}
                onClick={() => { navigate(item.to); setOpen(false); }}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
              >
                <span className="text-base flex-shrink-0">{item.icon}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{item.label}</p>
                  <p className="text-[11px] text-slate-400 truncate">{item.description}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Platform Settings (admins only) */}
          {isPlatformAdmin && (
            <>
              <div className="px-3 py-2 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-purple-500">
                  Platform Administration
                </p>
              </div>
              <div className="py-1">
                {PLATFORM_SETTINGS.map((item) => (
                  <button
                    key={item.to}
                    onClick={() => { navigate(item.to); setOpen(false); }}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-purple-50 transition-colors"
                  >
                    <span className="text-base flex-shrink-0">{item.icon}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{item.label}</p>
                      <p className="text-[11px] text-slate-400 truncate">{item.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Footer links */}
          <div className="border-t border-slate-100 px-3 py-2">
            <button
              onClick={() => { navigate('/integration-docs'); setOpen(false); }}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-slate-500 hover:bg-slate-50"
            >
              <span>📚</span>
              <span>Integration Docs & API Reference</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
